/**
 * ServiceObdTab — thin wrapper around <OBDTab/> for the Service Ops Modal.
 *
 * Background (2026-05-27):
 * The OBD2 tab on the Inspections Live Progress Modal is feature-complete (toggle,
 * scan history, AI analysis, PDF download, confidence panel, manual codes…).
 * We needed parity on the Service Ops Modal without forking 900 lines.
 *
 * Strategy:
 *   • `OBDTab.jsx` was parameterised with an `apiBase` prop (defaults to
 *     `/inspections` so the existing inspection tab keeps working unchanged).
 *   • This wrapper passes `apiBase="/service-jobs"` and re-shapes the service
 *     lead document into the prop names OBDTab expects (`inspection`,
 *     `liveProgressData`, `dtcCodes`).
 *
 * Backend endpoints (all mirror the inspection equivalents — see
 * routes/service_obd.py):
 *     PATCH /api/service-jobs/{id}/obd-scanning
 *     GET   /api/service-jobs/{id}/obd-scan-history
 *     PATCH /api/service-jobs/{id}/obd-active-scan
 *     GET/PUT /api/service-jobs/{id}/obd-manual-codes
 *     POST  /api/service-jobs/{id}/obd-ai-analyze
 *     POST  /api/service-jobs/{id}/dtc-report-selection
 *     GET   /api/service-jobs/{id}/obd-confidence
 *     POST  /api/service-jobs/{id}/obd-pdf-upload   (in routes/obd_pdf.py)
 *     GET   /api/service-jobs/{id}/obd-pdf-job/{id}
 *     POST  /api/service-jobs/{id}/obd-codes/pdf
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs } from '../../ui/tabs';
import OBDTab from '../../LiveProgressModal/OBDTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ServiceObdTab = ({ lead, user, onChanged, ...rest }) => {
  const canEdit = true;  // CEO/Ops/CountryHead reach this modal; same RBAC as inspection OBD tab
  const onRefresh = onChanged;
  const [requestingRescan, setRequestingRescan] = useState(false);

  // Re-shape the service-lead document into the props OBDTab expects.
  // OBDTab reads:
  //   inspection.id, inspection.obd_scan, inspection.obd_scanning_enabled,
  //   inspection.obd_ai_analysis, inspection.car_number, inspection.brand,
  //   inspection.model, inspection.year
  const inspectionShape = useMemo(() => {
    if (!lead) return null;
    return {
      id: lead.id,
      obd_scan: lead.obd_scan || null,
      obd_scanning_enabled: lead.obd_scanning_enabled !== false,
      obd_completed: lead.obd_completed || false,
      obd_ai_analysis: lead.obd_ai_analysis || null,
      car_number: lead.vehicle_number || lead.car_number || '',
      rc_number: lead.rc_number || lead.vehicle_number || '',
      brand: lead.vehicle_brand || lead.brand || '',
      model: lead.vehicle_model || lead.model || '',
      year: lead.vehicle_year || lead.year || '',
      status: lead.status,
    };
  }, [lead]);

  // Live-progress shape used by OBDTab for raw obd_scan rendering.
  const liveProgressData = useMemo(() => ({
    obd_scan: lead?.obd_scan
      ? { data: lead.obd_scan, scanned_at: lead.obd_scanned_at || lead.obd_scan?.scanned_at }
      : null,
  }), [lead]);

  // DTC codes prop — OBDTab uses this for the analyzer input fallback.
  const dtcCodes = useMemo(() => {
    const scan = lead?.obd_scan || {};
    return scan.dtc_codes || scan.raw_codes || [];
  }, [lead]);

  const requestObdRescan = async () => {
    // Service-side rescan = re-enable obd_scanning_enabled so the mechanic
    // app blocks Mark Complete until a fresh scan is uploaded.
    if (!lead?.id) return;
    setRequestingRescan(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/service-jobs/${lead.id}/obd-scanning`,
        { enabled: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Rescan requested — mechanic must complete a new OBD scan.');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('rescan request failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to request rescan');
    } finally {
      setRequestingRescan(false);
    }
  };

  if (!lead) return null;

  return (
    <div data-testid="service-obd-tab" className="p-4">
      {/*
       * <Tabs value="obd"> is required because OBDTab internally wraps its
       * body in <TabsContent value="obd"> (it was designed to live inside the
       * inspection LiveProgressModal's <Tabs>). Without this, shadcn's
       * TabsContent throws: "`TabsContent` must be used within `Tabs`".
       * The p-4 padding compensates for the parent Service Ops Modal tab area
       * (no padding), matching how the inspection tab gets breathing room.
       */}
      <Tabs value="obd" className="w-full">
        <OBDTab
          canEdit={canEdit}
          inspection={inspectionShape}
          liveProgressData={liveProgressData}
          dtcCodes={dtcCodes}
          requestingRescan={requestingRescan}
          requestObdRescan={requestObdRescan}
          onRefresh={onRefresh}
          apiBase="/service-jobs"
        />
      </Tabs>
    </div>
  );
};

export default ServiceObdTab;
